import type { Localizer } from "@/main/react/Types";
import type { SensitiveUnlockKind } from "@/main/common/SensitiveUnlockStore";

export type ContentWarningOptions = {
    isSensitive?: boolean | null;
    isSpoiler?: boolean | null;
};

export type PageContentWarningOptions = ContentWarningOptions & {
    /**
     * What the visitor is being asked to consent to (`PROFILE-4`).
     *
     * <p>One generic pair used to serve all four `SensitiveUnlockKind`s, and its help line read "This
     * page is marked as sensitive." — the title in the passive voice, so only its second clause
     * carried anything. The word throughout was "page", on a surface where the thing behind the scrim
     * is a profile, a group, a series or a post. Naming it is what makes the line do work the title
     * does not already do.
     *
     * <p>Optional, and the generic line stays as the fallback: a caller that passes no kind renders
     * exactly as before. `SensitiveUnlockKind` is a closed union, so the per-kind set cannot go stale
     * behind a new surface without the compiler saying so.
     *
     * <p>Only the HELP line is per-kind. The title is deliberately identical across the four, because
     * it is the line the badge above it has to agree with, and the subject itself is already named on
     * the gate by `subjectLabel` (2026-08-17).
     */
    kind?: SensitiveUnlockKind | null;
};

const PER_KIND_SENSITIVE_HELP = {
    discussion: "content-warning.page.sensitive.discussion.help",
    user: "content-warning.page.sensitive.user.help",
    group: "content-warning.page.sensitive.group.help",
    series: "content-warning.page.sensitive.series.help",
} as const;

export type MaskedContentWarningCopy = {
    badgeLabel: string;
    label: string;
    helperText: string;
    revealText: string;
    /**
     * Label for the route out of the HIDE dead end. Under the HIDE preference the guard offers no
     * reveal control by design, so before 2026-08-14 a masked region gave the visitor nothing at all
     * to act on (`GROUP-1`, `DISCUSSION-19`). The destination is the viewer's own content preferences
     * whichever warning branch produced the mask, so this is one key rather than three.
     */
    preferencesRouteText: string;
};

export type PageContentWarningCopy = {
    badgeLabel: string;
    title: string;
    helperText: string;
    revealText: string;
    showSensitivePreferenceAction: boolean;
};

export function getMaskedContentWarningCopy(
    localizer: Localizer,
    { isSensitive = false, isSpoiler = false }: ContentWarningOptions,
): MaskedContentWarningCopy {
    if (isSensitive && isSpoiler) {
        return {
            badgeLabel: localizer("content-warning.badge.sensitive_spoiler"),
            label: localizer("content-warning.mask.sensitive_spoiler.label"),
            helperText: localizer("content-warning.mask.sensitive_spoiler.help"),
            revealText: localizer("content-warning.reveal.sensitive_spoiler"),
            preferencesRouteText: localizer("content-warning.mask.preferences-route"),
        };
    }

    if (isSpoiler) {
        return {
            badgeLabel: localizer("content-warning.badge.spoiler"),
            label: localizer("content-warning.mask.spoiler.label"),
            helperText: localizer("content-warning.mask.spoiler.help"),
            revealText: localizer("content-warning.reveal.spoiler"),
            preferencesRouteText: localizer("content-warning.mask.preferences-route"),
        };
    }

    return {
        badgeLabel: localizer("create.discussion.sensitive.label"),
        label: localizer("content-warning.mask.sensitive.label"),
        helperText: localizer("content-warning.mask.sensitive.help"),
        revealText: localizer("content-warning.reveal.sensitive"),
        preferencesRouteText: localizer("content-warning.mask.preferences-route"),
    };
}

export function getPageContentWarningCopy(
    localizer: Localizer,
    { isSensitive = false, isSpoiler = false, kind = null }: PageContentWarningOptions,
): PageContentWarningCopy {
    if (isSensitive && isSpoiler) {
        return {
            badgeLabel: localizer("content-warning.badge.sensitive_spoiler"),
            title: localizer("content-warning.page.sensitive_spoiler.title"),
            helperText: localizer("content-warning.page.sensitive_spoiler.help"),
            revealText: localizer("content-warning.page.reveal"),
            showSensitivePreferenceAction: true,
        };
    }

    if (isSpoiler) {
        return {
            badgeLabel: localizer("content-warning.badge.spoiler"),
            title: localizer("content-warning.page.spoiler.title"),
            helperText: localizer("content-warning.page.spoiler.help"),
            revealText: localizer("content-warning.page.reveal"),
            showSensitivePreferenceAction: false,
        };
    }

    return {
        badgeLabel: localizer("create.discussion.sensitive.label"),
        title: localizer("content-warning.page.sensitive.title"),
        helperText: kind
            ? localizer(PER_KIND_SENSITIVE_HELP[kind])
            : localizer("content-warning.page.sensitive.help"),
        revealText: localizer("content-warning.page.reveal"),
        showSensitivePreferenceAction: true,
    };
}
